<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const currentUser = computed(() => getters.getCurrentUser.value);
const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isAIHandling = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.AI_HANDLING
);
const hasAssignee = computed(
  () => currentChat.value.meta?.assignee?.id !== undefined
);
const isAssignedToMe = computed(
  () =>
    hasAssignee.value &&
    currentChat.value.meta.assignee.id === currentUser.value?.id
);

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const toggleStatus = (status, snoozedUntil) => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  // Not used anymore - we only toggle between open and ai_handling
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      // Convert resolve to open
      await toggleStatus(wootConstants.STATUS_TYPE.OPEN);
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      // Convert resolve to open
      await toggleStatus(wootConstants.STATUS_TYPE.OPEN);

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="relative flex items-center justify-end resolve-actions">
    <!-- When open and assigned to me: Show only dropdown button with "Change to AI Handling" -->
    <div
      v-if="isOpen && isAssignedToMe"
      class="rounded-lg shadow outline-1 outline outline-n-container flex-shrink-0"
    >
      <Button
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        color="slate"
        trailing-icon
        class="!outline-0 rounded-lg"
        @click="openDropdown"
      />
    </div>
    <!-- When ai_handling: Show "Change to Open" button -->
    <div
      v-else-if="isAIHandling"
      class="rounded-lg shadow outline-1 outline outline-transparent flex-shrink-0"
    >
      <Button
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        class="!outline-0 rounded-lg"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
    </div>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <!-- Show "Change to AI Handling" when in Mine tab (open status and assigned to me) -->
        <WootDropdownItem v-if="isOpen && isAssignedToMe">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.CHANGE_TO_AI')"
            ghost
            slate
            sm
            start
            icon="i-lucide-sparkles"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.AI_HANDLING)"
          />
        </WootDropdownItem>
        <!-- Note: "Change to Open" is shown as main button when ai_handling, not in dropdown -->
      </WootDropdownMenu>
    </div>
  </div>
</template>
